import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

// NOTE: Avoid importing from '../../components' barrel here.
// The barrel re-exports many components and can hide circular dependencies that break SSR chunk execution.
import NamedLink from '../../components/NamedLink/NamedLink';
import LayoutComposer from '../../components/LayoutComposer';
import { Heading } from '../../components/Heading/Heading';

import StaticPage from '../PageBuilder/StaticPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { renderMarkdown } from '../../util/markdown';
import { useConfiguration } from '../../context/configurationContext';

import {
  fetchBlogPost,
  fetchPageByUrlPath,
  getBlogPost,
  getBlogPostFetchInProgress,
  getBlogPostFetchError,
} from './BlogPostPage.duck';

import css from './BlogPostPage.module.css';

const ssrDebug = typeof window === 'undefined' && process.env.SSR_DEBUG === 'true';
// eslint-disable-next-line no-console
if (ssrDebug) console.log('[SSR_DEBUG] BlogPostPage module loaded');

export const BlogPostPageComponent = props => {
  // eslint-disable-next-line no-console
  if (ssrDebug) console.log('[SSR_DEBUG] BlogPostPageComponent render start');
  const {
    post = null,
    fetchInProgress = false,
    fetchError = null,
    fetchBlogPost,
    params = {},
    history,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const marketplaceRootURL = config?.marketplaceRootURL || '';
  const currentUser = useSelector(state => state.user.currentUser);
  const isAdmin = currentUser?.attributes?.profile?.publicData?.userType === 'admin';

  const handlePrimaryCTA = () => {
    // Navigate to search page
    if (history && typeof history.push === 'function') {
      history.push('/products');
    }
  };

  useEffect(() => {
    // Avoid refetching on client hydration if SSR already loaded the correct post.
    // This prevents a brief "loading" flash + double network requests.
    const slug = params?.slug;
    const urlPath = params?.urlPath;

    const hasCorrectPost =
      (slug && post?.slug && post.slug === slug) ||
      (urlPath && post?.page_type === 'page' && post?.url_path && post.url_path === urlPath);

    if (slug && config && !hasCorrectPost && !fetchInProgress) {
      fetchBlogPost(slug, config);
    }
    if (urlPath && config && !hasCorrectPost && !fetchInProgress) {
      fetchPageByUrlPath(urlPath, config);
    }
  }, [params?.slug, params?.urlPath, config, post?.slug, post?.url_path, post?.page_type, fetchInProgress, fetchBlogPost, fetchPageByUrlPath]);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  if (fetchError) {
    return (
      <StaticPage
        className={css.root}
        title={intl.formatMessage({ id: 'BlogPostPage.errorTitle' })}
        scrollingDisabled={false}
      >
        <LayoutComposer areas={layoutAreas} className={css.layout}>
          {() => (
            <>
              <TopbarContainer />
              <main className={css.content}>
                <div className={css.error}>
                  <h2>
                    <FormattedMessage id="BlogPostPage.errorTitle" />
                  </h2>
                  <p>{fetchError.message}</p>
                </div>
              </main>
              <FooterContainer />
            </>
          )}
        </LayoutComposer>
      </StaticPage>
    );
  }

  if (fetchInProgress || !post) {
    return (
      <StaticPage
        className={css.root}
        title={intl.formatMessage({ id: 'BlogPostPage.loading' })}
        scrollingDisabled={false}
      >
        <LayoutComposer areas={layoutAreas} className={css.layout}>
          {() => (
            <>
              <TopbarContainer />
              <main className={css.content}>
                <div className={css.loading}>
                  <FormattedMessage id="BlogPostPage.loading" />
                </div>
              </main>
              <FooterContainer />
            </>
          )}
        </LayoutComposer>
      </StaticPage>
    );
  }

  const metaTitle = post.meta_title || post.title;
  const metaDescription = post.meta_description || post.excerpt || '';
  const ogImage = post.og_image_url || post.featured_image_url || '';

  const publishedDate = post.published_at ? new Date(post.published_at).toISOString() : '';
  const modifiedDate = post.updated_at ? new Date(post.updated_at).toISOString() : '';
  const authorName = post.author_name || post.author || 'Neokasa';
  const openGraphType = post.page_type === 'page' ? 'website' : 'article';

  // Schema.org JSON-LD - adapter selon le type
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': post.page_type === 'page' ? 'WebPage' : 'BlogPosting',
    headline: post.title,
    description: post.excerpt || '',
    image: ogImage,
    ...(post.page_type === 'blog' ? {
      datePublished: publishedDate,
      dateModified: modifiedDate,
      author: {
        '@type': 'Person',
        name: authorName,
      },
    } : {}),
    publisher: {
      '@type': 'Organization',
      name: config.marketplaceName || 'Neokasa',
      logo: {
        '@type': 'ImageObject',
        url: `${marketplaceRootURL}/static/icons/favicon.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${marketplaceRootURL}${post.page_type === 'page' ? `/${post.url_path}` : `/blog/${post.slug}`}`,
    },
  };

  // Breadcrumb adapté selon le type
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: marketplaceRootURL,
      },
      ...(post.page_type === 'blog' ? [{
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${marketplaceRootURL}/blog`,
      }] : []),
      {
        '@type': 'ListItem',
        position: post.page_type === 'blog' ? 3 : 2,
        name: post.title,
        item: `${marketplaceRootURL}${post.page_type === 'page' ? `/${post.url_path}` : `/blog/${post.slug}`}`,
      },
    ],
  };

  const articleTitle = post.title;
  const articleDescription = metaDescription;

  return (
    <StaticPage
      className={css.root}
      title={metaTitle}
      description={metaDescription}
      author={authorName}
      openGraphType={openGraphType}
      published={post.page_type === 'blog' ? publishedDate : undefined}
      updated={post.page_type === 'blog' ? modifiedDate : undefined}
      robots="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      schema={[schemaData, breadcrumbSchema]}
      socialSharing={{
        title: metaTitle,
        description: metaDescription,
        // Keep social image explicit if provided, otherwise Page will fall back to marketplace defaults.
        ...(ogImage
          ? {
              images1200: [{ url: ogImage, width: 1200, height: 630 }],
              images600: [{ url: ogImage, width: 600, height: 314 }],
            }
          : {}),
      }}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {() => (
          <>
            <TopbarContainer />
            <main className={css.content}>
              <article className={css.article}>
                <header className={css.articleHeader}>
                  <div className={css.headerTop}>
                    {post.page_type === 'blog' && (
                      <NamedLink name="BlogListPage" className={css.backLinkHeader}>
                        ← <FormattedMessage id="BlogPostPage.backToBlog" />
                      </NamedLink>
                    )}
                    {isAdmin && post?.slug && (
                      <NamedLink
                        name="BlogPostEditorEdit"
                        params={{ slug: post.slug }}
                        className={css.editButton}
                      >
                        <FormattedMessage id="BlogPostPage.edit" />
                      </NamedLink>
                    )}
                  </div>
                  <div className={css.articleMeta}>
                    {post.categories && post.categories.length > 0 && (
                      <span className={css.category}>
                        {post.categories[0].name}
                      </span>
                    )}
                    {post.published_at && (
                      <span className={css.date}>
                        {intl.formatDate(new Date(post.published_at), {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <Heading as="h1" rootClassName={css.articleTitle}>
                    {post.title}
                  </Heading>
                  {post.excerpt && (
                    <p className={css.articleExcerpt}>
                      {post.excerpt}
                    </p>
                  )}
                </header>

                <div className={css.articleContent}>
                  {post.content_format === 'markdown' && post.content_html ? (
                    <div dangerouslySetInnerHTML={{ __html: post.content_html }} />
                  ) : post.content_format === 'markdown' && post.content ? (
                    // Fallback (client-side / legacy). Avoid relying on this for SSR stability.
                    renderMarkdown(post.content)
                  ) : post.content ? (
                    <div dangerouslySetInnerHTML={{ __html: post.content }} />
                  ) : null}
                </div>

                {/* CTA Section */}
                <section className={css.ctaSection}>
                  <div className={css.ctaContent}>
                    <h2 className={css.ctaTitle}>Prêt à commencer ?</h2>
                    <p className={css.ctaSubtitle}>
                      Rejoignez des milliers de passionnés de maison connectée et transformez votre espace de vie dès aujourd'hui
                    </p>
                    <button className={css.ctaButton} onClick={handlePrimaryCTA}>
                      Découvrir notre catalogue domotique
                    </button>
                  </div>
                </section>
              </article>
            </main>
            <FooterContainer />
          </>
        )}
      </LayoutComposer>
    </StaticPage>
  );
};

const mapStateToProps = state => {
  return {
    post: getBlogPost(state),
    fetchInProgress: getBlogPostFetchInProgress(state),
    fetchError: getBlogPostFetchError(state),
  };
};

const mapDispatchToProps = {
  fetchBlogPost,
  fetchPageByUrlPath,
};

const BlogPostPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(BlogPostPageComponent);

export default BlogPostPage;

